import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit, Trash2, X, Package, TrendingUp, Gift, Star, Box, XCircle, Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import * as XLSX from 'xlsx';
import { PLACEHOLDER_IMAGE } from '../../lib/placeholder';



const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [discountError, setDiscountError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [isExportingCatalog, setIsExportingCatalog] = useState(false);



  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);
    return pages;
  };

  const [formData, setFormData] = useState({
    name: '',
    short_description: '',
    description: '',

    price: '',
    original_price: '',
    category_id: '',
    subcategory: '',
    brand: '',
    stock: '',
    existingImages: [],
    newImageFiles: [],
    specifications: {},
    tags: [],
    is_trending: false,
    cgst: '0',
    sgst: '0',
    igst: '0',
    meta_title: '',
    meta_keywords: '',
    meta_description: ''
  });


  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '12' });
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (categoryFilter && categoryFilter !== 'all') params.append('category_id', categoryFilter);
      if (stockFilter && stockFilter !== 'all') params.append('stock_status', stockFilter);


      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/products?${params}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
        setTotalPages(data.pages || 1);
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, categoryFilter, stockFilter]);


  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/categories`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to page 1 on search change
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle auto-calculation of price based on original price and discount
  useEffect(() => {
    const originalPrice = parseFloat(formData.original_price);
    const discountValue = parseFloat(formData.discount_value);

    if (isNaN(originalPrice)) {
      setDiscountError(false);
      return;
    }

    let isError = false;
    if (formData.discount_type === 'flat' && !isNaN(discountValue) && discountValue > originalPrice) {
      isError = true;
    } else if (formData.discount_type === 'percentage' && !isNaN(discountValue) && discountValue > 100) {
      isError = true;
    }

    if (isError) {
      if (!discountError) {
        toast.error('Invalid Discount', {
          description: formData.discount_type === 'flat'
            ? 'Discount cannot exceed original price!'
            : 'Discount percentage cannot exceed 100%!',
          duration: 3000,
        });
      }
      setDiscountError(true);
    } else {
      setDiscountError(false);
    }

    let calculatedPrice = originalPrice;

    if (formData.discount_type === 'flat' && !isNaN(discountValue)) {
      calculatedPrice = Math.max(0, originalPrice - discountValue);
    } else if (formData.discount_type === 'percentage' && !isNaN(discountValue)) {
      calculatedPrice = Math.max(0, originalPrice - (originalPrice * (discountValue / 100)));
    }

    const roundedPrice = Math.round(calculatedPrice).toString();
    if (formData.discount_type === 'b1g1') {
      // For B1G1, if price is set to '0' or is empty, we default it to original price 
      // (since you pay for 1 to get 1 free)
      if (formData.price === '0' || formData.price === '') {
        setFormData(prev => ({ ...prev, price: originalPrice.toString() }));
      }
    } else if (formData.price !== roundedPrice) {
      setFormData(prev => ({ ...prev, price: roundedPrice }));
    }
  }, [formData.original_price, formData.discount_type, formData.discount_value, discountError]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (discountError) {
      toast.error('Please fix the discount errors before saving.');
      return;
    }

    setLoading(true);


    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('short_description', formData.short_description);
      data.append('description', formData.description);
      data.append('discount_type', formData.discount_type);
      data.append('discount_amount', formData.discount_value);
      data.append('discount_value', formData.discount_value);
      data.append('price', formData.price);
      if (formData.original_price) data.append('original_price', formData.original_price);
      data.append('category_id', formData.category_id);
      data.append('subcategory', formData.subcategory);
      data.append('brand', formData.brand);
      data.append('stock', formData.stock);
      data.append('specifications', JSON.stringify(formData.specifications));
      data.append('tags', JSON.stringify(formData.tags));
      data.append('is_featured', formData.is_featured);
      data.append('is_bestseller', formData.is_bestseller);
      data.append('is_trending', formData.is_trending);
      data.append('cgst', formData.cgst);
      data.append('sgst', formData.sgst);
      data.append('igst', formData.igst);
      if (formData.meta_title) data.append('meta_title', formData.meta_title);
      if (formData.meta_keywords) data.append('meta_keywords', formData.meta_keywords);
      if (formData.meta_description) data.append('meta_description', formData.meta_description);


      if (editingProduct) {
        data.append('existing_images', JSON.stringify(formData.existingImages));
        formData.newImageFiles.forEach(file => {
          data.append('new_images', file);
        });
      } else {
        formData.newImageFiles.forEach(file => {
          data.append('images', file);
        });
      }

      const url = editingProduct
        ? `${API}/products/${editingProduct.product_id}`
        : `${API}/products`;

      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: data
      });

      if (!response.ok) throw new Error('Failed to save product');

      toast.success(editingProduct ? 'Product updated' : 'Product created');
      await fetchProducts();
      resetForm();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/products/${productId}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete product');

      toast.success('Product deleted');
      await fetchProducts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (product) => {
    let derivedDiscount = product.discount_amount || product.discount_value || '';
    if ((!derivedDiscount || Number(derivedDiscount) === 0) && product.discount_type !== 'none' && product.discount_type !== 'b1g1') {
      const op = Number(product.original_price);
      const p = Number(product.price);
      if (op > p) {
        if (product.discount_type === 'flat') {
          derivedDiscount = (op - p).toString();
        } else if (product.discount_type === 'percentage') {
          derivedDiscount = Math.round(((op - p) / op) * 100).toString();
        }
      }
    }

    setFormData({
      name: product.name,
      short_description: product.short_description || '',
      description: product.description,
      discount_type: product.discount_type || 'none',
      discount_value: derivedDiscount,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      category_id: product.category_id,
      subcategory: product.subcategory || '',
      brand: product.brand || '',
      stock: product.stock.toString(),
      existingImages: product.images || [],
      newImageFiles: [],
      specifications: product.specifications || {},
      tags: product.tags || [],
      is_featured: product.is_featured,
      is_bestseller: product.is_bestseller,
      is_trending: product.is_trending,
      cgst: product.cgst?.toString() || '0',
      sgst: product.sgst?.toString() || '0',
      igst: product.igst?.toString() || '0',
      meta_title: product.meta_title || '',
      meta_keywords: product.meta_keywords || '',
      meta_description: product.meta_description || ''
    });

    setEditingProduct(product);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      short_description: '',
      description: '',
      discount_type: 'none',
      discount_value: '',
      price: '',
      original_price: '',
      category_id: '',
      subcategory: '',
      brand: '',
      stock: '',
      existingImages: [],
      newImageFiles: [],
      specifications: {},
      tags: [],
      is_featured: false,
      is_bestseller: false,
      is_trending: false,
      cgst: '0',
      sgst: '0',
      igst: '0',
      meta_title: '',
      meta_keywords: '',
      meta_description: ''
    });

    setEditingProduct(null);
    setShowForm(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData({ ...formData, newImageFiles: [...formData.newImageFiles, ...files] });
  };

  const handleExport = async () => {
    try {
      setIsExportingCatalog(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (categoryFilter && categoryFilter !== 'all') params.append('category_id', categoryFilter);
      if (stockFilter && stockFilter !== 'all') params.append('stock_status', stockFilter);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const response = await fetch(`${API}/admin/products/export${queryString}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to export catalog');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Catalog_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Catalog exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export catalog');
    } finally {
      setIsExportingCatalog(false);
    }
  };

  const handleImport = async (file) => {
    setImporting(true);
    setImportProgress({ current: 0, total: 100 });
    try {
      const data = new FormData();
      data.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/products/bulk-import`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: data
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Bulk import failed');
      }

      const result = await response.json();
      toast.success(result.message || 'Catalog imported successfully');
      setShowImportModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Import failure:', error);
      toast.error(error.message || 'Failed to parse or upload file');
    } finally {
      setImporting(false);
    }
  };


  const removeNewImage = (index) => {

    const newFiles = [...formData.newImageFiles];
    newFiles.splice(index, 1);
    setFormData({ ...formData, newImageFiles: newFiles });
  };

  const removeExistingImage = (index) => {
    const images = [...formData.existingImages];
    images.splice(index, 1);
    setFormData({ ...formData, existingImages: images });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <div className="space-y-8 pb-10" data-testid="admin-products">
      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="font-heading text-3xl text-secondary mb-1">Products Management</h1>
          <p className="text-muted-foreground font-body text-sm">Manage your inventory, pricing, and product visibility.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExportingCatalog}
            className="rounded-full border-blue-200 bg-blue-50/20 text-blue-700 hover:bg-blue-50/50 px-6 font-bold text-xs uppercase tracking-[0.1em] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExportingCatalog ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Catalog
              </>
            )}
          </Button>

          <Button
            onClick={() => setShowImportModal(true)}
            className="rounded-full bg-surface-secondary text-primary-foreground hover:bg-surface-secondary/90 px-6 font-bold text-xs uppercase tracking-[0.1em] border border-primary/20 bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-100 transition-all hover:scale-105"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>

          <Button
            onClick={() => setShowForm(true)}
            className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 px-6 font-bold text-xs uppercase tracking-[0.1em] ring-offset-2 focus:ring-2 ring-primary/20 transition-all hover:scale-105"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Product
          </Button>
        </div>


      </div>

      {/* Quick Stats: Premium Dashboard Experience */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { id: 'all', label: 'Total Products', icon: Box, color: 'text-blue-600', bgColor: 'bg-blue-50', count: stats ? stats.total_products : products.length },
          { id: 'active', label: 'Active Items', icon: TrendingUp, color: 'text-emerald-600', bgColor: 'bg-emerald-50', count: stats ? stats.active : products.length },
          { id: 'low', label: 'Low Stock', icon: Gift, color: 'text-orange-500', bgColor: 'bg-orange-50', count: stats ? stats.low_stock : products.filter(p => p.stock > 0 && p.stock <= 10).length },
          { id: 'out', label: 'Out of Stock', icon: XCircle, color: 'text-rose-500', bgColor: 'bg-rose-50/50', count: stats ? stats.out_of_stock : products.filter(p => p.stock === 0).length },
        ].map((stat, i) => (
          <div
            key={i}
            onClick={() => setStockFilter(stat.id === stockFilter ? 'all' : stat.id)}
            className={`cursor-pointer bg-white rounded-[2rem] p-6 shadow-sm border ${stockFilter === stat.id ? 'border-primary ring-4 ring-primary/5' : 'border-gray-100/50'} hover:shadow-md transition-all flex items-center gap-5`}
          >

            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${stat.bgColor} flex-shrink-0 animate-in zoom-in-50 duration-500 delay-75`}>
              <stat.icon className={`w-7 h-7 ${stat.color}`} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground/60 mb-0.5 leading-none">{stat.label}</p>
              <p className="text-3xl font-heading font-black text-secondary leading-tight">{stat.count}</p>
            </div>
          </div>
        ))}
      </div>


      {/* Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-soft border border-gray-50 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, brand or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 pr-4 py-6 rounded-xl border-gray-100 focus:ring-primary/20 bg-surface/50"
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full md:w-52 rounded-xl border-gray-100 py-6 bg-surface/50">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="rounded-xl shadow-2xl border-gray-100">
              <SelectItem value="all">Catalog Overview</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.category_id} value={cat.category_id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={stockFilter} onValueChange={setStockFilter}>
            <SelectTrigger className="w-full md:w-52 rounded-xl border-gray-100 py-6 bg-surface/50">
              <SelectValue placeholder="Stock Status" />
            </SelectTrigger>
            <SelectContent className="rounded-xl shadow-2xl border-gray-100">
              <SelectItem value="all">Any Availability</SelectItem>
              <SelectItem value="in">In Stock (Normal)</SelectItem>
              <SelectItem value="low">Low Stock Alert</SelectItem>
              <SelectItem value="out">Out of Stock</SelectItem>
            </SelectContent>
          </Select>
        </div>

      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl text-secondary">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h2>
                <p className="text-muted-foreground text-sm font-body">Fill in the details below to {editingProduct ? 'update' : 'create'} the product.</p>
              </div>
              <button onClick={resetForm} className="p-2 hover:bg-surface rounded-full transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium ml-1">Product Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g. Classic Black Frame"
                    className="rounded-xl py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium ml-1">Brand</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="e.g. Adidas"
                    className="rounded-xl py-6"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium ml-1">Short Description</Label>
                <div className="bg-white rounded-xl overflow-hidden border border-gray-200">
                  <ReactQuill
                    theme="snow"
                    value={formData.short_description}
                    onChange={(content) => setFormData({ ...formData, short_description: content })}
                    placeholder="Brief overview of the product..."
                    className="h-32 mb-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium ml-1">Long Description</Label>
                <div className="bg-white rounded-xl overflow-hidden border border-gray-200 min-h-[300px]">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(content) => setFormData({ ...formData, description: content })}
                    placeholder="Detailed product features and specifications..."
                    className="h-64 mb-12"
                  />
                </div>
              </div>

              {/* Unified Pricing and Offers Section */}
              <div className="p-8 bg-surface/30 rounded-3xl border border-gray-100 space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-6 bg-primary rounded-full" />
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-secondary">Pricing & Offers</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
                  {/* Original Price */}
                  <div className="space-y-2.5">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">Original Price (₹)</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 font-medium">₹</span>
                      <Input
                        type="number"
                        value={formData.original_price}
                        onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                        className="rounded-2xl py-7 pl-10 bg-white border-gray-100 focus:border-primary/30 focus:ring-primary/10 transition-all font-bold"
                        placeholder="e.g. 2000"
                      />
                    </div>
                  </div>

                  {/* Discount Type */}
                  <div className="space-y-2.5">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">Discount Type</Label>
                    <Select
                      value={formData.discount_type || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                    >
                      <SelectTrigger className="rounded-2xl py-7 bg-white border-gray-100 focus:border-primary/30 focus:ring-primary/10 transition-all">
                        <SelectValue placeholder="No Discount" />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl shadow-xl border-gray-100">
                        <SelectItem value="none">No Discount</SelectItem>
                        <SelectItem value="flat">Flat Amount (₹)</SelectItem>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="b1g1">Buy 1 Get 1 Free</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Discount Value/Amount */}
                  <div className="space-y-2.5">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">
                      {formData.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={formData.discount_value || ''}
                        disabled={formData.discount_type === 'none' || formData.discount_type === 'b1g1'}
                        onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                        placeholder={formData.discount_type === 'flat' ? 'e.g. 500' : formData.discount_type === 'percentage' ? 'e.g. 10' : '---'}
                        className={`rounded-2xl py-7 bg-white transition-all font-bold ${discountError ? 'border-destructive ring-4 ring-destructive/10' : 'border-gray-100 focus:border-primary/30 focus:ring-primary/10'}`}
                        data-testid="discount-value"
                      />
                      {formData.discount_type !== 'none' && formData.discount_type !== 'b1g1' && (
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/30 font-medium">
                          {formData.discount_type === 'flat' ? '₹' : '%'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Final Price */}
                  <div className="space-y-2.5">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-primary ml-1">Final Price (₹)</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-primary/50 font-bold">₹</span>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                        className="rounded-2xl py-7 pl-10 bg-primary/5 border-primary/20 text-primary font-black text-lg focus:border-primary/40 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                </div>

                {formData.discount_type === 'b1g1' && (
                  <div className="flex items-center gap-3 bg-indigo-50/50 rounded-2xl p-4 border border-indigo-100/50 animate-in fade-in slide-in-from-top-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Package className="w-4 h-4 text-indigo-600" />
                    </div>
                    <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Buy 1 Get 1 Free Offer Enabled</p>
                  </div>
                )}
              </div>

              {/* Taxation Section */}
              <div className="p-8 bg-surface/30 rounded-3xl border border-gray-100 space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-6 bg-blue-500 rounded-full" />
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-secondary">Taxation (GST %)</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* CGST */}
                  <div className="space-y-2.5">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">CGST (%)</Label>
                    <Input
                      type="number"
                      value={formData.cgst}
                      onChange={(e) => {
                        const val = e.target.value;
                        const sgst = formData.sgst;
                        setFormData({
                          ...formData,
                          cgst: val,
                          igst: (parseFloat(val || 0) + parseFloat(sgst || 0)).toString()
                        });
                      }}
                      className="rounded-2xl py-7 bg-white border-gray-100"
                      placeholder="e.g. 9"
                    />
                  </div>

                  {/* SGST */}
                  <div className="space-y-2.5">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">SGST (%)</Label>
                    <Input
                      type="number"
                      value={formData.sgst}
                      onChange={(e) => {
                        const val = e.target.value;
                        const cgst = formData.cgst;
                        setFormData({
                          ...formData,
                          sgst: val,
                          igst: (parseFloat(cgst || 0) + parseFloat(val || 0)).toString()
                        });
                      }}
                      className="rounded-2xl py-7 bg-white border-gray-100"
                      placeholder="e.g. 9"
                    />
                  </div>

                  {/* IGST */}
                  <div className="space-y-2.5">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-blue-600 ml-1">IGST (%)</Label>
                    <Input
                      type="number"
                      value={formData.igst}
                      onChange={(e) => {
                        const val = e.target.value;
                        const half = (parseFloat(val || 0) / 2).toString();
                        setFormData({
                          ...formData,
                          igst: val,
                          cgst: half,
                          sgst: half
                        });
                      }}
                      className="rounded-2xl py-7 bg-blue-50/50 border-blue-100 text-blue-700 font-bold"
                      placeholder="e.g. 18"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground italic ml-1">
                  * IGST is automatically calculated as CGST + SGST. For inter-state orders, only IGST applies.
                </p>
              </div>


              {/* Inventory Management Row */}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-2.5 p-6 bg-surface/20 rounded-3xl border border-gray-100">
                  <div className="flex items-center justify-between">
                    <Label className="text-[11px] font-black uppercase tracking-widest text-secondary ml-1">Inventory Control</Label>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${formData.stock > 10 ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                      Initial Stock
                    </span>
                  </div>
                  <Input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    required
                    className="rounded-2xl py-7 bg-white border-gray-100 focus:border-primary/30 focus:ring-primary/10 transition-all font-bold"
                    placeholder="Total units available..."
                  />
                </div>

                <div className="space-y-2.5">
                  <Label className="text-[11px] font-black uppercase tracking-widest text-secondary ml-1">Category Placement</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger className="rounded-2xl py-7 bg-white border-gray-100">
                      <SelectValue placeholder="Select primary category" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      {categories.map((cat) => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2.5">
                <Label className="text-[11px] font-black uppercase tracking-widest text-secondary ml-1">Subcategory / Product Type</Label>
                <Input
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className="rounded-2xl py-7 bg-white border-gray-100"
                  placeholder="e.g. Sunglasses, Optical Frames, etc."
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium ml-1">Product Gallery</Label>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
                  {/* Existing Images */}
                  {formData.existingImages.map((img, idx) => (
                    <div key={`existing-${idx}`} className="relative group aspect-square">
                      <img
                        src={getImageUrl(img)}
                        alt=""
                        className="w-full h-full object-cover rounded-2xl border border-gray-100 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(idx)}
                        className="absolute -top-2 -right-2 bg-destructive text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}

                  {/* New Images */}
                  {formData.newImageFiles.map((file, idx) => (
                    <div key={`new-${idx}`} className="relative group aspect-square">
                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="w-full h-full object-cover rounded-2xl border border-gray-100 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewImage(idx)}
                        className="absolute -top-2 -right-2 bg-destructive text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}

                  {/* Add Image Button */}
                  <label className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-gray-200 rounded-2xl hover:border-primary hover:bg-surface/50 transition-all cursor-pointer group">
                    <Plus className="w-6 h-6 text-muted-foreground group-hover:text-primary mb-1 transition-colors" />
                    <span className="text-[10px] text-muted-foreground group-hover:text-primary font-body uppercase tracking-wider">Upload</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>

              <div className="bg-surface/50 p-4 rounded-2xl flex flex-wrap gap-8 justify-between">
                {[
                  { id: 'is_featured', label: 'Featured', icon: Star },
                  { id: 'is_bestseller', label: 'Bestseller', icon: TrendingUp },
                  { id: 'is_trending', label: 'Trending', icon: Package }
                ].map((toggle) => (
                  <div key={toggle.id} className="flex items-center gap-3">
                    <Switch
                      checked={formData[toggle.id]}
                      onCheckedChange={(checked) => setFormData({ ...formData, [toggle.id]: checked })}
                    />
                    <div className="flex items-center gap-2">
                      <toggle.icon className="w-4 h-4 text-muted-foreground" />
                      <Label className="text-sm font-medium cursor-pointer">{toggle.label}</Label>
                    </div>
                  </div>
                ))}
              </div>

              {/* SEO Metadata Section */}
              <div className="p-8 bg-surface/30 rounded-3xl border border-gray-100 space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-secondary">SEO Metadata</h3>
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 ml-2">Optional</span>
                </div>
                <p className="text-xs text-muted-foreground italic -mt-2 ml-1">These fields help improve your product's search engine ranking. Leave empty to auto-generate from product name and description.</p>

                <div className="space-y-2.5">
                  <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">Meta Title</Label>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    className="rounded-2xl py-7 bg-white border-gray-100 focus:border-emerald-300 focus:ring-emerald-100 transition-all"
                    placeholder={formData.name ? `${formData.name} | NSO India` : 'e.g. Ray-Ban Aviator Classic | NSO India'}
                    maxLength={70}
                  />
                  <p className="text-[10px] text-muted-foreground ml-1">{formData.meta_title.length}/70 characters (recommended: 50-60)</p>
                </div>

                <div className="space-y-2.5">
                  <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">Meta Keywords</Label>
                  <Input
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                    className="rounded-2xl py-7 bg-white border-gray-100 focus:border-emerald-300 focus:ring-emerald-100 transition-all"
                    placeholder="e.g. ray-ban, aviator, sunglasses, optical, eyewear"
                  />
                  <p className="text-[10px] text-muted-foreground ml-1">Comma-separated keywords for search engines</p>
                </div>

                <div className="space-y-2.5">
                  <Label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">Meta Description</Label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="rounded-2xl py-4 bg-white border-gray-100 focus:border-emerald-300 focus:ring-emerald-100 transition-all min-h-[100px]"
                    placeholder={formData.name ? `Buy ${formData.name} online at NSO India. Best price, genuine product with warranty. Free shipping on orders above ₹999.` : 'Brief SEO description for search results (150-160 chars recommended)...'}
                    maxLength={160}
                  />
                  <p className="text-[10px] text-muted-foreground ml-1">{formData.meta_description.length}/160 characters (recommended: 120-155)</p>
                </div>
              </div>

              <div className="flex gap-4 pt-6 border-t border-gray-100">
                <Button type="submit" disabled={loading} className="flex-1 rounded-full bg-primary py-7 text-lg font-medium shadow-xl shadow-primary/20">
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </div>
                  ) : (editingProduct ? 'Update Product Details' : 'Launch New Product')}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="px-8 rounded-full py-7 border-gray-200 hover:bg-surface transition-colors">
                  Discard
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products Table Container */}
      <div className="bg-white rounded-[2rem] shadow-soft border border-gray-50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-surface/30">
                <th className="text-left py-6 px-6 font-body font-bold text-secondary text-xs uppercase tracking-widest">Product Info</th>
                <th className="text-left py-6 px-4 font-body font-bold text-secondary text-xs uppercase tracking-widest">Category</th>
                <th className="text-left py-6 px-4 font-body font-bold text-secondary text-xs uppercase tracking-widest">Pricing</th>
                <th className="text-left py-6 px-4 font-body font-bold text-secondary text-xs uppercase tracking-widest">Offers</th>
                <th className="text-left py-6 px-4 font-body font-bold text-secondary text-xs uppercase tracking-widest">Inventory</th>

                <th className="text-left py-6 px-4 font-body font-bold text-secondary text-xs uppercase tracking-widest">Badges</th>
                <th className="text-right py-6 px-6 font-body font-bold text-secondary text-xs uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && !products.length ? (
                <tr>
                  <td colSpan={6} className="py-24 text-center">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                      <p className="text-muted-foreground font-body font-medium">Fetching your catalog...</p>
                    </div>
                  </td>
                </tr>
              ) : (
                (() => {
                  const items = products.filter(product => {
                    if (stockFilter === 'all') return true;
                    if (stockFilter === 'active') return product.stock > 0;
                    if (stockFilter === 'low') return product.stock > 0 && product.stock <= 10;
                    if (stockFilter === 'out') return product.stock === 0;
                    if (stockFilter === 'in') return product.stock > 10;
                    return true;
                  });

                  if (items.length === 0) {
                    return (
                      <tr>
                        <td colSpan={7} className="py-24 text-center">
                          <div className="flex flex-col items-center justify-center gap-4 opacity-40">
                            <Box className="w-16 h-16 text-muted-foreground" />
                            <p className="text-muted-foreground font-heading text-xl font-bold">No products found matching filters</p>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return items.map((product) => (
                    <tr key={product.product_id} className="group hover:bg-surface/50 transition-colors">
                      <td className="py-5 px-6 max-w-[250px] sm:max-w-[300px] lg:max-w-[400px]">
                        <div className="flex items-center gap-4 w-full">
                          <div className="relative w-14 h-14 rounded-2xl border border-gray-100 overflow-hidden bg-surface/50 shadow-sm flex-shrink-0">
                            <img
                              src={getImageUrl(product.images?.[0])}
                              alt={product.name}
                              className="w-full h-full object-contain p-1 group-hover:scale-110 transition-transform duration-500"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-body font-bold text-secondary truncate text-base leading-tight">
                              {product.name}
                            </p>
                            <p className="font-body text-xs text-muted-foreground uppercase tracking-widest mt-1 font-semibold">
                              {product.brand || 'No Brand'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-4 font-body text-sm text-secondary/80 capitalize font-medium">
                        {product.subcategory || product.category_id?.replace('cat_', '') || 'General'}
                      </td>
                      <td className="py-5 px-4">
                        <p className="font-body font-bold text-secondary text-lg">{formatPrice(product.price)}</p>
                        {product.original_price && (
                          <p className="font-body text-xs text-muted-foreground line-through opacity-60">
                            {formatPrice(product.original_price)}
                          </p>
                        )}
                      </td>
                      <td className="py-5 px-4 font-body text-sm">

                        {product.discount_type && product.discount_type !== 'none' ? (
                          <div className="flex flex-col gap-1">
                            {(() => {
                              let derived = product.discount_amount || product.discount_value || 0;
                              if (!derived && product.original_price > product.price) {
                                if (product.discount_type === 'flat') derived = product.original_price - product.price;
                                else if (product.discount_type === 'percentage') derived = Math.round(((product.original_price - product.price) / product.original_price) * 100);
                              }
                              return (
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${product.discount_type === 'b1g1' ? 'bg-indigo-500/10 text-indigo-600' : 'bg-primary/10 text-primary'}`}>
                                  {product.discount_type === 'b1g1' ? 'Buy 1 Get 1 Free' :
                                    product.discount_type === 'flat' ? `₹${derived} OFF` :
                                      `${derived}% OFF`}
                                </span>
                              );
                            })()}
                          </div>
                        ) : (
                          <span className="text-muted-foreground/40 text-[10px] uppercase font-bold tracking-widest">Standard Price</span>
                        )}
                      </td>
                      <td className="py-5 px-4">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${product.stock > 10 ? 'bg-success' : product.stock > 0 ? 'bg-warning' : 'bg-destructive'
                              }`} />
                            <span className={`text-[11px] font-bold font-body uppercase tracking-wider ${product.stock > 10 ? 'text-success' : product.stock > 0 ? 'text-warning' : 'text-destructive'
                              }`}>
                              {product.stock > 10 ? 'In Stock' : product.stock > 0 ? 'Low Stock' : 'Out of Stock'}
                            </span>
                          </div>
                          <p className="text-base font-body font-bold text-secondary">{product.stock} Units</p>
                          {product.stock > 0 && product.stock <= 10 && (
                            <div className="w-full bg-warning/10 h-1 rounded-full overflow-hidden mt-1">
                              <div className="bg-warning h-full" style={{ width: `${(product.stock / 10) * 100}%` }} />
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="py-5 px-4">
                        <div className="flex gap-1.5 flex-wrap">
                          {product.is_featured && (
                            <span className="bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider">Featured</span>
                          )}
                          {product.is_bestseller && (
                            <span className="bg-success/10 text-success text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider">Top Seller</span>
                          )}
                          {product.is_trending && (
                            <span className="bg-purple-500/10 text-purple-600 text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider">Trending</span>
                          )}
                          {!product.is_featured && !product.is_bestseller && !product.is_trending && (
                            <span className="text-muted-foreground text-[10px] font-medium px-2 py-0.5 bg-surface rounded-md">Standard</span>
                          )}
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex justify-end gap-3 translate-x-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <button
                            onClick={() => handleEdit(product)}
                            className="w-10 h-10 flex items-center justify-center bg-surface hover:bg-primary/10 text-muted-foreground hover:text-primary rounded-xl transition-all"
                            title="Edit Product"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.product_id)}
                            className="w-10 h-10 flex items-center justify-center bg-surface hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-xl transition-all"
                            title="Delete Product"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ));
                })()
              )}
            </tbody>
          </table>
        </div>

        {/* Improved Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-6 bg-surface/20 border-t border-gray-100">
            <p className="text-sm text-muted-foreground font-body">
              Showing page <span className="font-bold text-secondary">{page}</span> of <span className="font-bold text-secondary">{totalPages}</span>
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="rounded-xl px-4 py-2 border-gray-200"
              >
                Previous
              </Button>
              <div className="flex gap-1 flex-wrap justify-center">
                {getPageNumbers().map((p, idx) => (
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground flex items-center font-body text-sm">…</span>
                  ) : (
                    <button
                      key={`page-${p}`}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 rounded-xl font-body text-sm font-bold transition-all ${page === p
                        ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-110'
                        : 'bg-white text-secondary hover:bg-surface border border-gray-100 hover:border-gray-200'
                        }`}
                    >
                      {p}
                    </button>
                  )
                ))}
              </div>
              <Button
                variant="outline"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="rounded-xl px-4 py-2 border-gray-200"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md transition-all animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-xl rounded-[3rem] p-10 shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => !importing && setShowImportModal(false)}
              className="absolute top-8 right-8 p-3 hover:bg-surface rounded-full transition-colors text-muted-foreground hover:text-secondary"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="space-y-8">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center text-primary mb-2">
                  <FileSpreadsheet className="w-10 h-10" />
                </div>
                <h2 className="text-3xl font-heading font-black text-secondary">Bulk Import Products</h2>
                <p className="text-muted-foreground font-body max-w-xs">
                  Upload your Excel (.xlsx) or CSV file to update your catalog in seconds.
                </p>
              </div>

              {importing ? (
                <div className="space-y-6 py-6 px-4 bg-surface/50 rounded-3xl border border-gray-100">
                  <div className="flex items-center justify-between text-sm font-bold uppercase tracking-widest text-secondary mb-2">
                    <span>Processing Catalog</span>
                    <span className="text-primary">{Math.round((importProgress.current / (importProgress.total || 1)) * 100)}%</span>
                  </div>
                  <div className="w-full h-4 bg-gray-100/50 rounded-full overflow-hidden border border-gray-100">
                    <div
                      className="h-full bg-primary transition-all duration-300 ease-out"
                      style={{ width: `${(importProgress.current / (importProgress.total || 1)) * 100}%` }}
                    />
                  </div>
                  <p className="text-center text-xs text-muted-foreground font-medium italic">
                    Successfully processed {importProgress.current} of {importProgress.total} items...
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <label className="flex flex-col items-center justify-center w-full py-16 border-2 border-dashed border-gray-200 rounded-[2.5rem] bg-surface/30 hover:bg-surface/50 hover:border-primary transition-all cursor-pointer group group">
                    <Upload className="w-10 h-10 text-muted-foreground group-hover:text-primary mb-4 transition-all group-hover:-translate-y-1" />
                    <span className="text-sm font-bold text-secondary mb-1">Select Catalog File</span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Excel or CSV files only</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".xlsx, .xls, .csv"
                      onChange={(e) => e.target.files?.[0] && handleImport(e.target.files[0])}
                    />
                  </label>

                  <div className="grid grid-cols-1 gap-3 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50">
                    <p className="text-[10px] font-black uppercase tracking-widest text-blue-700 mb-1 flex items-center gap-2">
                      <AlertCircle className="w-3 h-3" />
                      Pro Tips for Success
                    </p>
                    <ul className="text-[11px] text-blue-800 space-y-1.5 opacity-80">
                      <li>• Required columns: "Product Name", "Brand", "Price", "Stock", "Category", "Subcategory".</li>
                      <li>• Prices and Stock must be numeric digits only.</li>
                      <li>• Ensure category names already exist in the system.</li>
                      <li>• Supported formats: Excel (.xlsx) or CSV.</li>
                    </ul>
                  </div>
                </div>
              )}

              {!importing && (
                <Button
                  onClick={() => setShowImportModal(false)}
                  variant="outline"
                  className="w-full rounded-2xl py-6 font-bold text-sm tracking-widest transition-all hover:bg-gray-50 border-gray-200"
                >
                  CANCEL AND RETURN
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
